import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StorefrontIcon, PushPinIcon, PushPinSlashIcon, HammerIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Seller-only Shop Hub for a live stream.
 *
 * Lists every listing the seller owns in this stream's segment; the
 * seller picks which one goes on-stage with an optional auction timer
 * and Buy-It-Now price, and hits Pin. The currently-pinned lot is
 * highlighted with an Unpin / Hammer action pair.
 *
 * Only the pinned lot accepts bids or Buy-Now on the server, so this
 * panel IS the single control surface for what viewers can transact on.
 */
export default function ShopHub({ open, onClose, stream, pinnedListingId, onChange }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(30);
  const [buyNow, setBuyNow] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/me/listings", { params: { limit: 50 } });
        const mine = (r.data?.items || []).filter((l) => l.segment === stream?.segment && l.status !== "sold");
        setListings(mine);
      } catch (e) {
        toast.error(apiError(e, "Couldn't load your listings"));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, stream?.segment]);

  const pin = async (listing) => {
    setBusyId(listing.id);
    try {
      const body = { listing_id: listing.id };
      if (listing.type === "auction" && duration > 0) {
        body.auction_duration_seconds = parseInt(duration, 10) || 30;
      }
      if (buyNow && !isNaN(parseFloat(buyNow))) {
        body.buy_now_price = parseFloat(buyNow);
      }
      await api.post(`/streams/${stream.id}/pin`, body);
      toast.success("On stage. Viewers can bid now.");
      onChange?.();
      onClose?.();
    } catch (e) {
      toast.error(apiError(e, "Couldn't pin"));
    } finally {
      setBusyId(null);
    }
  };

  const unpin = async () => {
    setBusyId("__unpin");
    try {
      await api.post(`/streams/${stream.id}/unpin`);
      onChange?.();
    } catch (e) {
      toast.error(apiError(e, "Couldn't unpin"));
    } finally {
      setBusyId(null);
    }
  };

  const hammer = async () => {
    if (!window.confirm("Hammer / sell the current lot? This is final.")) return;
    setBusyId("__hammer");
    try {
      const r = await api.post(`/streams/${stream.id}/hammer`);
      if (r.data?.winner_id) {
        toast.success(`Sold to ${r.data.winner_id}. Order created.`);
      } else {
        toast.info("No bidders — lot closed without a buyer.");
      }
      onChange?.();
    } catch (e) {
      toast.error(apiError(e, "Couldn't hammer"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="shop-hub-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StorefrontIcon size={20} className="text-[#1D78AB]" weight="fill" />
            Shop hub — pick the next lot
          </DialogTitle>
        </DialogHeader>

        {pinnedListingId && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between gap-2">
            <div className="text-xs text-emerald-900">
              <b>On stage now.</b> Bids + Buy-Now are live for the pinned lot.
            </div>
            <div className="flex gap-2">
              <Button
                size="sm" variant="outline" onClick={unpin}
                disabled={!!busyId}
                data-testid="shop-hub-unpin-btn"
              >
                <PushPinSlashIcon size={14} className="mr-1" /> Unpin
              </Button>
              <Button
                size="sm" onClick={hammer}
                disabled={!!busyId}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="shop-hub-hammer-btn"
              >
                <HammerIcon size={14} className="mr-1" /> Hammer
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 text-xs">
          <label>
            Auction length
            <Select
              value={String(duration)}
              onValueChange={(v) => setDuration(parseInt(v, 10))}
            >
              <SelectTrigger className="mt-1" data-testid="shop-hub-duration-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2 seconds</SelectItem>
                <SelectItem value="5">5 seconds</SelectItem>
                <SelectItem value="10">10 seconds</SelectItem>
                <SelectItem value="30">30 seconds</SelectItem>
                <SelectItem value="60">1 minute</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label>
            Buy-It-Now price (optional)
            <Input
              type="number" step="0.01"
              value={buyNow}
              onChange={(e) => setBuyNow(e.target.value)}
              placeholder="e.g. 149.00"
              data-testid="shop-hub-buy-now-input"
            />
          </label>
        </div>

        <div className="max-h-[50vh] overflow-y-auto divide-y divide-zinc-200">
          {loading && <div className="p-4 text-sm text-zinc-500">Loading…</div>}
          {!loading && listings.length === 0 && (
            <div className="p-4 text-sm text-zinc-500">
              No listings in your segment. Create some from Dashboard first.
            </div>
          )}
          {listings.map((l) => {
            const isPinned = l.id === pinnedListingId;
            return (
              <div
                key={l.id}
                className={`py-2 flex items-center gap-3 ${isPinned ? "bg-emerald-50/50" : ""}`}
                data-testid={`shop-hub-row-${l.id}`}
              >
                {l.image_urls?.[0] && (
                  <img src={l.image_urls[0]} alt="" className="h-12 w-12 rounded-md object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{l.title}</div>
                  <div className="text-xs text-zinc-500 font-mono">
                    {l.type === "auction" ? `Auction · start $${l.starting_bid || 0}` : `Fixed · $${l.price || 0}`}
                    {" · "}{l.status}
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={isPinned || busyId === l.id}
                  onClick={() => pin(l)}
                  className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
                  data-testid={`shop-hub-pin-${l.id}`}
                >
                  <PushPinIcon size={14} className="mr-1" weight="fill" />
                  {isPinned ? "Pinned" : "Pin"}
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
